import { User } from '@authy/lib-common/entities'
import { serialize, deserialize } from '@authy/lib-common/utils/misc'

import { client, TRPCClientError } from '@/trpc'

import { LoadCreateUserFunc } from './types'

const tryLoadUser = async (
    forceLoad: boolean,
    update: (user: User | null) => void,
    tries: number = 0
): Promise<void> => {
    try {
        if (!forceLoad) {
            // Try to read from cache first and if has then only refresh and immediately return here
            const cached = localStorage.getItem('cache.user')
            if (cached) {
                try {
                    // Deserialize from cache
                    const user = deserialize(cached)

                    // Immediately update
                    update(user)

                    // Get fresh data in the background
                    client.currentUser.getUser
                        .query()
                        .then((user) => {
                            // Store updated user if any
                            localStorage.setItem('cache.user', serialize(user)!)

                            // Call update
                            update(user as User)
                        })
                        .catch((error) => {
                            throw error
                        })

                    // we're done here
                    return
                } catch {
                    // -- ignore and load regularily
                }
            }
        }

        // Await initial user
        const user = (await client.currentUser.getUser.query()) as User

        // Store updated user if any
        localStorage.setItem('cache.user', serialize(user)!)

        // Make initial update
        update(user)
    } catch (error) {
        // retry on dev env until the server has started
        if (
            import.meta.env.DEV &&
            error instanceof TRPCClientError &&
            error.message === 'Failed to fetch' &&
            tries < 30
        ) {
            await new Promise((resolve) => setTimeout(resolve, 1000))

            return tryLoadUser(true, update, tries + 1)
        }

        throw error
    }
}

export const loadCreateUser: LoadCreateUserFunc = async (forceLoad, update) => {
    try {
        await tryLoadUser(forceLoad, update)
    } catch (error) {
        if (error instanceof TRPCClientError) {
            if (error.data?.code === 'NOT_FOUND') {
                await client.currentUser.createUser.mutate()

                return tryLoadUser(true, update)
            }
        }

        throw error
    }
}

export const loadCreateUserNull: LoadCreateUserFunc = async (forceLoad, update) => update(null)
